<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
    value:  { required: true },
    depth:  { type: Number, default: 0 },
    label:  { type: String, default: null },
})

const open = ref(props.depth < 2)

const vtype = computed(() => {
    if (props.value === null)            return 'null'
    if (Array.isArray(props.value))      return 'array'
    return typeof props.value
})

const isContainer = computed(() => vtype.value === 'object' || vtype.value === 'array')
const entries     = computed(() => isContainer.value ? Object.entries(props.value ?? {}) : [])

const COLORS = {
    key:     '#e5c07b',
    string:  '#98c379',
    number:  '#d19a66',
    boolean: '#56b6c2',
    null:    '#c678dd',
    brace:   '#abb2bf',
    count:   '#636d83',
    toggle:  '#636d83',
}

function valueColor(v) {
    if (v === null)            return COLORS.null
    if (typeof v === 'string') return COLORS.string
    if (typeof v === 'number') return COLORS.number
    if (typeof v === 'boolean') return COLORS.boolean
    return COLORS.brace
}

function displayValue(v) {
    if (v === null)            return 'null'
    if (typeof v === 'string') return `"${v}"`
    return String(v)
}

function preview(v) {
    if (Array.isArray(v))            return `[…${v.length}]`
    if (typeof v === 'object' && v)  return `{…${Object.keys(v).length}}`
    return ''
}
</script>

<template>
    <span>
        <!-- Object / Array container -->
        <template v-if="isContainer">
            <!-- Toggle button -->
            <button @click.stop="open = !open"
                class="cursor-pointer select-none font-mono"
                :style="{ color: COLORS.toggle }">{{ open ? '▾' : '▸' }}</button>

            <!-- Collapsed preview -->
            <span v-if="!open" :style="{ color: COLORS.count }" class="font-mono text-[11px] ml-1">
                {{ preview(value) }}
            </span>

            <!-- Expanded entries -->
            <template v-if="open">
                <span :style="{ color: COLORS.brace }" class="font-mono">{{ vtype === 'array' ? '[' : '{' }}</span>
                <div class="ml-5">
                    <div v-for="([k, v], i) in entries" :key="k" class="leading-relaxed">
                        <!-- Key -->
                        <span :style="{ color: COLORS.key }" class="font-mono">"{{ k }}"</span>
                        <span :style="{ color: COLORS.brace }" class="font-mono">: </span>
                        <!-- Value (recursive) -->
                        <DevJsonTree :value="v" :depth="depth + 1" />
                        <span v-if="i < entries.length - 1" :style="{ color: COLORS.brace }" class="font-mono">,</span>
                    </div>
                </div>
                <span :style="{ color: COLORS.brace }" class="font-mono">{{ vtype === 'array' ? ']' : '}' }}</span>
            </template>
        </template>

        <!-- Primitive -->
        <span v-else :style="{ color: valueColor(value) }" class="font-mono break-all">{{ displayValue(value) }}</span>
    </span>
</template>
